import { c as _c } from "react/compiler-runtime";
import type { ApiNode, GraphOperation, GraphOperationAddNode, GraphOperationPasteNode } from '@onefootprint/request-types/dashboard';
import type { Banner } from '../components/error-warning-list/error-warning-list';
import useStore from './use-store';
type RemoveNodeOperation = Extract<GraphOperation, {
  kind: 'remove_node';
}>;
export type AddOperation = GraphOperationAddNode | GraphOperationPasteNode;
export type NodeContext = {
  node: ApiNode | undefined;
  /** The operation to remove this node, if any. */
  removeOperation: RemoveNodeOperation | undefined;
  /** The list of operations that would add a node directly before this node, if any. */
  addBeforeNodeOperations: AddOperation[];
  /** The list of operations that would add a node directly after this node, if any. */
  addAfterNodeOperations: AddOperation[];
  nodeErrors: Banner[];
  nodeColorClassName?: string;
  nodeBackgroundClassName?: 'bg-error' | 'bg-info';
  isInSubgraph: boolean;
  successors: string[];
};
const useNodeContext = nodeId => {
  const $ = _c(36);
  const {
    data: t0,
    visibleBanners,
    executionTraceBanners
  } = useStore();
  const {
    graph,
    nodeHighlight,
    canEdit
  } = t0;
  let t1;
  if ($[0] !== canEdit || $[1] !== graph.availableOperations) {
    t1 = canEdit ? graph.availableOperations ?? [] : [];
    $[0] = canEdit;
    $[1] = graph.availableOperations;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const availableOperations = t1;
  let t2;
  if ($[3] !== availableOperations || $[4] !== executionTraceBanners || $[5] !== graph.nodes || $[6] !== nodeHighlight || $[7] !== nodeId || $[8] !== visibleBanners) {
    let t3;
    if ($[10] !== nodeId) {
      t3 = node => node.id === nodeId;
      $[10] = nodeId;
      $[11] = t3;
    } else {
      t3 = $[11];
    }
    const node_0 = graph.nodes.find(t3);
    let t4;
    if ($[12] !== executionTraceBanners || $[13] !== visibleBanners) {
      t4 = [...visibleBanners, ...executionTraceBanners];
      $[12] = executionTraceBanners;
      $[13] = visibleBanners;
      $[14] = t4;
    } else {
      t4 = $[14];
    }
    let t5;
    if ($[15] !== nodeId || $[16] !== t4) {
      t5 = t4.filter(item => item.nodeId === nodeId);
      $[15] = nodeId;
      $[16] = t4;
      $[17] = t5;
    } else {
      t5 = $[17];
    }
    const allNodeErrors = t5;
    let t6;
    if ($[18] !== availableOperations || $[19] !== nodeId) {
      let t7;
      if ($[21] !== nodeId) {
        t7 = op => op.kind === "remove_node" && op.nodeId === nodeId;
        $[21] = nodeId;
        $[22] = t7;
      } else {
        t7 = $[22];
      }
      t6 = availableOperations.find(t7);
      $[18] = availableOperations;
      $[19] = nodeId;
      $[20] = t6;
    } else {
      t6 = $[20];
    }
    const removeOperation = t6;
    let t7;
    if ($[23] !== availableOperations || $[24] !== nodeId) {
      t7 = availableOperations?.filter(op_0 => (op_0.kind === "add_node" || op_0.kind === "paste_node") && op_0.childNodeId === nodeId && !op_0.parentNodeId) || [];
      $[23] = availableOperations;
      $[24] = nodeId;
      $[25] = t7;
    } else {
      t7 = $[25];
    }
    const addBeforeNodeOperations = t7;
    let t8;
    if ($[26] !== availableOperations || $[27] !== nodeId) {
      t8 = availableOperations?.filter(op_1 => (op_1.kind === "add_node" || op_1.kind === "paste_node") && op_1.parentNodeId === nodeId) || [];
      $[26] = availableOperations;
      $[27] = nodeId;
      $[28] = t8;
    } else {
      t8 = $[28];
    }
    const addAfterNodeOperations = t8;
    let t9;
    if ($[29] !== allNodeErrors || $[30] !== nodeHighlight || $[31] !== nodeId) {
      const getNodeColorClassName = () => {
        if (nodeHighlight?.ids.includes(nodeId)) {
          return {
            error: "border-error",
            info: "border-info"
          }[nodeHighlight.color];
        }
        if (allNodeErrors.some(_temp)) {
          return "border-error";
        }
        if (allNodeErrors.some(_temp2)) {
          return "border-warning";
        }
      };
      t9 = getNodeColorClassName();
      $[29] = allNodeErrors;
      $[30] = nodeHighlight;
      $[31] = nodeId;
      $[32] = t9;
    } else {
      t9 = $[32];
    }
    const nodeColorClassName = t9;
    let t10;
    if ($[33] !== nodeHighlight || $[34] !== nodeId) {
      const getNodeBackgroundClassName = () => {
        if (nodeHighlight?.ids.includes(nodeId) && nodeHighlight?.variant === "border-and-background") {
          const backgroundClasses = {
            error: "bg-error",
            info: "bg-info"
          };
          return backgroundClasses[nodeHighlight.color];
        }
      };
      t10 = getNodeBackgroundClassName();
      $[33] = nodeHighlight;
      $[34] = nodeId;
      $[35] = t10;
    } else {
      t10 = $[35];
    }
    const nodeBackgroundClassName = t10;
    t2 = {
      node: node_0,
      removeOperation,
      addBeforeNodeOperations,
      addAfterNodeOperations,
      nodeErrors: allNodeErrors,
      nodeColorClassName,
      nodeBackgroundClassName,
      isInSubgraph: !!node_0?.subgraphId,
      successors: getSuccessors(node_0)
    };
    $[3] = availableOperations;
    $[4] = executionTraceBanners;
    $[5] = graph.nodes;
    $[6] = nodeHighlight;
    $[7] = nodeId;
    $[8] = visibleBanners;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
};
export const getSuccessors = (node?: ApiNode) => {
  if (node?.kind === 'branch') {
    return [...node.cases.map(c => c.branch.successor), node.defaultBranch.successor];
  }
  if (node?.kind === 'conditional') {
    return [...node.conditionalBranches.map(c => c.branch.successor), node.defaultBranch.successor];
  }
  if (node !== undefined) {
    return node.successor ? [node.successor] : [];
  }
  return [];
};
export default useNodeContext;
function _temp(item_0) {
  return item_0.type === "error";
}
function _temp2(item_1) {
  return item_1.type === "warning";
}